import React, { useState, useEffect } from 'react';
import { CheckSquare, Trash2, Square, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../utils/cn';
import { Button } from '../components/Button';
import { GoogleTasksSync } from '../components/GoogleTasksSync';
import { supabase } from '../lib/supabase';
import { ProtectedLayout } from '../components/ProtectedLayout';

export function TaskList() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionStreak, setActionStreak] = useState(0);
  const [showStreakMessage, setShowStreakMessage] = useState(false);

  useEffect(() => {
    async function fetchTasks() {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) {
          navigate('/');
          return;
        }

        const { data: tasksData, error: tasksError } = await supabase
          .from('tasks')
          .select('*')
          .eq('user_id', session.user.id)
          .order('created_at', { ascending: false });

        if (tasksError) throw tasksError;
        setTasks(tasksData || []);

        // Calculate streak
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const startOfYesterday = new Date(startOfToday);
        startOfYesterday.setDate(startOfYesterday.getDate() - 1);

        let streak = 0;
        let currentDate = startOfYesterday;

        while (true) {
          const hasCompletedTask = tasksData?.some(task => {
            if (!task.completed || !task.completed_at) return false;
            const taskDate = new Date(task.completed_at);
            return taskDate.toDateString() === currentDate.toDateString();
          });
          if (!hasCompletedTask) break;
          streak++;
          currentDate.setDate(currentDate.getDate() - 1);
        }

        const hasCompletedToday = tasksData?.some(task => {
          if (!task.completed || !task.completed_at) return false;
          const taskDate = new Date(task.completed_at);
          return taskDate.toDateString() === startOfToday.toDateString();
        });

        setActionStreak(hasCompletedToday ? streak + 1 : streak);
      } catch (error: any) {
        console.error('Error fetching tasks:', error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    }

    fetchTasks();
  }, [navigate]);

  useEffect(() => {
    if (showStreakMessage) {
      const timeout = setTimeout(() => setShowStreakMessage(false), 4000);
      return () => clearTimeout(timeout);
    }
  }, [showStreakMessage]);

  const handleToggleTask = async (taskId: string, currentStatus: boolean) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const now = new Date().toISOString();

      const { error: taskError } = await supabase
        .from('tasks')
        .update({
          completed: !currentStatus,
          completed_at: !currentStatus ? now : null
        })
        .eq('id', taskId);

      if (taskError) throw taskError;

      const updatedTasks = tasks.map(task =>
        task.id === taskId
          ? { ...task, completed: !currentStatus, completed_at: !currentStatus ? now : null }
          : task
      );

      setTasks(updatedTasks);

      if (!currentStatus) {
        const today = new Date().toISOString().slice(0, 10);
        await supabase.from('growth_days').upsert(
          [{
            user_id: session.user.id,
            date: today,
            task_completed: true,
            updated_at: new Date().toISOString()
          }],
          { onConflict: ['user_id', 'date'] }
        );

        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        const alreadyCompletedToday = updatedTasks.some(task => {
          if (!task.completed || !task.completed_at) return false;
          const completedAt = new Date(task.completed_at);
          return completedAt.toDateString() === startOfToday.toDateString();
        });

        if (!alreadyCompletedToday) {
          setActionStreak(prev => prev + 1);
          setShowStreakMessage(true);
        }
      }
    } catch (error: any) {
      console.error('Error updating task:', error);
      setError(error.message);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    try {
      const { error } = await supabase.from('tasks').delete().eq('id', taskId);
      if (error) throw error;
      setTasks(prev => prev.filter(task => task.id !== taskId));
    } catch (error: any) {
      console.error('Error deleting task:', error);
      setError(error.message);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-blue-50 flex items-center justify-center">
        <div className="text-[#236caf]">Loading...</div>
      </div>
    );
  }

  const incompleteTasks = tasks.filter(task => !task.completed);
  const completedTasks = tasks.filter(task => task.completed);

  return (
     <ProtectedLayout>
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50">
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <CheckSquare className="w-8 h-8 text-[#236caf]" />
              <h1 className="text-3xl font-bold text-[#004060]">Meaningful Actions</h1>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Button>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg">{error}</div>
          )}

          {showStreakMessage && (
            <div className="mb-6 p-4 bg-green-50 text-green-700 rounded-lg">
              <p className="font-medium">
                Great job – that is {actionStreak} {actionStreak === 1 ? 'day' : 'days'} in a row of completing a meaningful action. Keep it up.
              </p>
            </div>
          )}

          {tasks.length === 0 ? (
            <p className="text-gray-500 italic">No Meaningful Actions yet. Complete your daily journal to get started.</p>
          ) : (
            <div className="space-y-4">
              {incompleteTasks.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-700 mb-3">To Do</h2>
                  <div className="space-y-2">
                    {incompleteTasks.map(task => (
                      <div key={task.id} className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 hover:border-[#236caf] transition-colors duration-200">
                        <button onClick={() => handleToggleTask(task.id, task.completed)} className="mt-1">
                          <Square className="w-5 h-5 text-gray-400 hover:text-[#236caf]" />
                        </button>
                        <p className="flex-1 text-gray-700">{task.text}</p>
                        <button onClick={() => handleDeleteTask(task.id)} className="text-gray-400 hover:text-red-500 transition-colors duration-200">
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {completedTasks.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-700 mb-3">Completed</h2>
                  <div className="space-y-2">
                    {completedTasks.map(task => (
                      <div key={task.id} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 border border-gray-200">
                        <button onClick={() => handleToggleTask(task.id, task.completed)} className="mt-1">
                          <CheckSquare className="w-5 h-5 text-[#318231]" />
                        </button>
                        <p className="flex-1 text-gray-500 line-through">{task.text}</p>
                        <button onClick={() => handleDeleteTask(task.id)} className="text-gray-400 hover:text-red-500 transition-colors duration-200">
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="mt-8 pt-8 border-t border-gray-200">
            <GoogleTasksSync />
          </div>
        </div>
      </div>
    </div>
     </ProtectedLayout>  
  );
}
